import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { confirmDiscardPendingOfflineData } from '../utils/offlineSync';
import {
  saveUserListToIndexedDB,
  getUserListFromIndexedDB,
  isCacheValid,
  clearUserListCache,
} from '../services/indexedDBService';

const clearServiceWorkerCache = async () => {
  if ('serviceWorker' in navigator && 'caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    } catch (error) {
      console.error('Error clearing service worker cache:', error);
    }
  }
};

const readTokenTenantId = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.activeTenantId ?? null;
  } catch {
    return null;
  }
};

const restoreAdminSession = (admin) => {
  // undefined means a ghost session saved before these fields existed, so leave whatever is stored
  const restore = (key, value) => {
    if (value === undefined) return;
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  };
  restore('refreshToken', admin?.refreshToken);
  restore('isSuperAdmin', admin?.isSuperAdmin);
  restore('currentTenantId', admin?.currentTenantId);
  restore('firstname', admin?.firstname);
  restore('lastname', admin?.lastname);
  restore('hubspot_user_id', admin?.hubspotUserId);
};

const clearAllUserCaches = async () => {
  // Clear session storage
  sessionStorage.clear();
  localStorage.removeItem('offlineSubmissions');

  // Clear all IndexedDB databases so the next user sees fresh data
  try {
    if ('databases' in indexedDB) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        dbs.map(db => {
          // Keep ghostUserDB — it holds admin's user list, not user-specific data
          if (db.name && db.name !== 'ghostUserDB') {
            return indexedDB.deleteDatabase(db.name);
          }
        })
      );
    }
  } catch (error) {
    console.error('Error clearing IndexedDB databases:', error);
  }

  await clearServiceWorkerCache();
};

const useGhostUser = () => {
  const { REACT_APP_BASE_URL } = process.env;
  const [isGhostMode, setIsGhostMode] = useState(false);
  const [ghostedUser, setGhostedUser] = useState(null);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  const [userList, setUserList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingGhostUser, setPendingGhostUser] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState("");
  // Populated when the target user isn't a member of the admin's active
  // tenant and has zero/multiple tenants of their own - the admin must pick
  // one before a ghost token can be issued (mirrors signin's tenant picker).
  const [ghostTenantChoices, setGhostTenantChoices] = useState(null);

  useEffect(() => {
    const ghostModeData = localStorage.getItem('ghostMode');
    if (ghostModeData) {
      try {
        const data = JSON.parse(ghostModeData);
        setIsGhostMode(true);
        setGhostedUser(data.ghostedUser);
        setOriginalAdmin(data.originalAdmin);
      } catch (error) {
        console.error('Error parsing ghost mode data:', error);
        localStorage.removeItem('ghostMode');
      }
    }
  }, []);

  const fetchUserList = useCallback(async (forceRefresh = false) => {
    setIsLoadingUsers(true);
    try {
      if (!forceRefresh && isCacheValid()) {
        const cachedUsers = await getUserListFromIndexedDB();
        if (cachedUsers && cachedUsers.length > 0) {
          setUserList(cachedUsers);
          setIsLoadingUsers(false);
          return cachedUsers;
        }
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(`${REACT_APP_BASE_URL}/admin/ghost-users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const users = response.data.results || [];
      await saveUserListToIndexedDB(users);
      
      const formattedUsers = users.map(user => ({
        userId: user.id,
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        email: user.email,
        avatar: user.avatar || '',
        role: user.role || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        hubspot_user_id: user.hubspot_user_id || '',
      }));
      
      setUserList(formattedUsers);
      return formattedUsers;
    } catch (error) {
      console.error('Error fetching user list:', error);
      toast.error('Failed to fetch user list');
      
      const cachedUsers = await getUserListFromIndexedDB();
      if (cachedUsers && cachedUsers.length > 0) {
        setUserList(cachedUsers);
        return cachedUsers;
      }
      return [];
    } finally {
      setIsLoadingUsers(false);
    }
  }, [REACT_APP_BASE_URL]);

  const initiateGhostMode = useCallback((user) => {
    setPendingGhostUser(user);
    setShowWarningModal(true);
  }, []);

  const confirmGhostMode = useCallback(async (targetTenantId = null) => {
    if (!pendingGhostUser) return;
    // Guards against callers wiring this directly to onClick (which passes
    // the SyntheticEvent as the first arg) - only a real id/string should
    // ever reach the request as targetTenantId.
    if (typeof targetTenantId !== 'number' && typeof targetTenantId !== 'string') {
      targetTenantId = null;
    }
    if (!(await confirmDiscardPendingOfflineData('switch to this user'))) return;

    try {
      const token = localStorage.getItem('token');
      const adminId = localStorage.getItem('admin_id');
      const currentUserEmail = localStorage.getItem('urserMail');
      const currentUserRole = localStorage.getItem('userRole');
      const adminSession = {
        refreshToken: localStorage.getItem('refreshToken'),
        isSuperAdmin: localStorage.getItem('isSuperAdmin'),
        currentTenantId: localStorage.getItem('currentTenantId'),
        firstname: localStorage.getItem('firstname'),
        lastname: localStorage.getItem('lastname'),
        hubspotUserId: localStorage.getItem('hubspot_user_id'),
      };

      const response = await axios.post(
        `${REACT_APP_BASE_URL}/admin/ghost-user-token`,
        {
          adminId: adminId,
          targetUserEmail: pendingGhostUser.email,
          ...(targetTenantId ? { targetTenantId } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.requiresTenantSelection) {
        setGhostTenantChoices(response.data.availableTenants || []);
        setShowWarningModal(false);
        return;
      }

      const ghostData = response.data;

      setOriginalAdmin({
        id: adminId,
        email: currentUserEmail,
        role: currentUserRole,
        token: token,
        ...adminSession,
      });

      setGhostedUser({
        id: ghostData.id,
        email: ghostData.email,
        username: ghostData.username,
        firstname: ghostData.firstname,
        lastname: ghostData.lastname,
        role: ghostData.roles,
      });

      localStorage.setItem('ghostMode', JSON.stringify({
        originalAdmin: {
          id: adminId,
          email: currentUserEmail,
          role: currentUserRole,
          token: token,
          ...adminSession,
        },
        ghostedUser: {
          id: ghostData.id,
          email: ghostData.email,
          username: ghostData.username,
          firstname: ghostData.firstname,
          lastname: ghostData.lastname,
          role: ghostData.roles,
        },
      }));

      localStorage.setItem('token', ghostData.token);
      localStorage.setItem('userId', ghostData.id);
      localStorage.setItem('urserMail', ghostData.email);
      localStorage.setItem('userRole', ghostData.roles);
      localStorage.setItem('username', ghostData.username);
      // the admin's refresh token and super admin flag must not outlive the switch, or a 401 would swap the admin back in
      localStorage.removeItem('refreshToken');
      localStorage.setItem('isSuperAdmin', 'false');
      const ghostTenantId = readTokenTenantId(ghostData.token);
      if (ghostTenantId !== null) {
        localStorage.setItem('currentTenantId', String(ghostTenantId));
      }

      ['firstname', 'lastname', 'hubspot_user_id'].forEach((key) => localStorage.removeItem(key));
      if (ghostData.firstname) {
        localStorage.setItem('firstname', ghostData.firstname);
      }
      if (ghostData.lastname) {
        localStorage.setItem('lastname', ghostData.lastname);
      }
      if (ghostData.hubspot_user_id) {
        localStorage.setItem('hubspot_user_id', ghostData.hubspot_user_id);
      }

      await clearUserListCache();
      await clearAllUserCaches();

      setIsGhostMode(true);
      setShowWarningModal(false);
      setPendingGhostUser(null);

      toast.success(`Now viewing as ${ghostData.email}`);
      setTransitionMessage(`Switching to ${ghostData.firstname ? ghostData.firstname + ' ' + ghostData.lastname : ghostData.email}...`);
      setIsTransitioning(true);
      setTimeout(() => {
        const url = new URL(window.location.origin + '/dashboard');
        url.searchParams.set('_reload', Date.now());
        window.location.replace(url.toString());
      }, 5000);
    } catch (error) {
      console.error('Error entering ghost mode:', error);
      toast.error(error.response?.data?.message || 'Failed to enter ghost mode');
      setShowWarningModal(false);
      setPendingGhostUser(null);
    }
  }, [pendingGhostUser, REACT_APP_BASE_URL]);

  const cancelGhostMode = useCallback(() => {
    setShowWarningModal(false);
    setPendingGhostUser(null);
    setGhostTenantChoices(null);
  }, []);

  const selectGhostTenant = useCallback((tenant) => {
    setGhostTenantChoices(null);
    return confirmGhostMode(tenant.id);
  }, [confirmGhostMode]);

  const cancelGhostTenantSelection = useCallback(() => {
    setGhostTenantChoices(null);
    setPendingGhostUser(null);
  }, []);

  const exitGhostMode = useCallback(async () => {
    if (!originalAdmin) {
      toast.error('Cannot exit ghost mode: Original admin data not found');
      return;
    }
    if (!(await confirmDiscardPendingOfflineData('exit ghost mode'))) return;

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${REACT_APP_BASE_URL}/admin/exit-ghost-mode`,
        {
          originalAdminId: originalAdmin.id,
          originalTenantId: originalAdmin.currentTenantId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const adminData = response.data;

      localStorage.setItem('token', adminData.token);
      localStorage.setItem('userId', adminData.id);
      localStorage.setItem('urserMail', adminData.email);
      localStorage.setItem('userRole', adminData.roles);
      localStorage.setItem('username', adminData.username);
      restoreAdminSession(originalAdmin);
      localStorage.removeItem('ghostMode');

      await clearUserListCache();
      await clearAllUserCaches();

      setIsGhostMode(false);
      setGhostedUser(null);
      setOriginalAdmin(null);

      toast.success('Exited ghost mode successfully');
      setTransitionMessage('Returning to your account...');
      setIsTransitioning(true);
      setTimeout(() => {
        const url = new URL(window.location.origin + '/dashboard');
        url.searchParams.set('_reload', Date.now());
        window.location.replace(url.toString());
      }, 5000);
    } catch (error) {
      console.error('Error exiting ghost mode:', error);
      toast.error('Failed to exit ghost mode');
    }
  }, [originalAdmin, REACT_APP_BASE_URL]);

  const refreshUserList = useCallback(async () => {
    await clearUserListCache();
    return fetchUserList(true);
  }, [fetchUserList]);

  return {
    isGhostMode,
    ghostedUser,
    originalAdmin,
    userList,
    isLoadingUsers,
    showWarningModal,
    pendingGhostUser,
    isTransitioning,
    transitionMessage,
    ghostTenantChoices,
    fetchUserList,
    initiateGhostMode,
    confirmGhostMode,
    cancelGhostMode,
    selectGhostTenant,
    cancelGhostTenantSelection,
    exitGhostMode,
    refreshUserList,
  };
};

export default useGhostUser;
